// 📄 components/credenciales/CredentialForm.tsx
import React, { useState } from 'react';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';
import { CredentialAmbiente, CredentialInput, CredentialMetadata } from '../../services/credentialService.ts';

interface CredentialFormProps {
  credential?: CredentialMetadata; // si existe, es edición
  onSave: (data: CredentialInput) => Promise<void>;
  onCancel: () => void;
}

const AMBIENTES: CredentialAmbiente[] = ['DEV', 'QAS', 'PRD', 'OTRO'];
const inputClass = 'w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm';
const labelClass = 'block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5';

export default function CredentialForm({ credential, onSave, onCancel }: CredentialFormProps) {
  const esEdicion = !!credential;
  const [nombre, setNombre] = useState(credential?.nombre || '');
  const [ambiente, setAmbiente] = useState<CredentialAmbiente>(credential?.ambiente || 'DEV');
  const [url, setUrl] = useState(credential?.url || '');
  const [usuario, setUsuario] = useState(credential?.usuario || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [nota, setNota] = useState(credential?.nota || '');
  const [compartida, setCompartida] = useState(credential?.compartida || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validar = (): string | null => {
    if (!nombre.trim()) return 'El nombre es obligatorio.';
    if (nombre.trim().length > 100) return 'El nombre no puede superar los 100 caracteres.';
    if (!usuario.trim()) return 'El usuario es obligatorio.';
    if (!esEdicion && !password.trim()) return 'La contraseña es obligatoria.';
    if (url.trim()) {
      try {
        new URL(url.trim());
      } catch {
        return 'La URL no tiene un formato válido (debe incluir https://).';
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validacion = validar();
    if (validacion) { setError(validacion); return; }

    setSaving(true);
    try {
      await onSave({ nombre: nombre.trim(), url: url.trim(), usuario: usuario.trim(), password, ambiente, nota: nota.trim(), compartida });
    } catch (err: any) {
      setError(err.message || 'No se pudo guardar la credencial.');
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-black/20 border border-white/10 rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Nombre</label>
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Proveedor Test Ariba" className={inputClass} maxLength={100} required />
        </div>
        <div>
          <label className={labelClass}>Ambiente</label>
          <select value={ambiente} onChange={(e) => setAmbiente(e.target.value as CredentialAmbiente)} className={inputClass}>
            {AMBIENTES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>URL</label>
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://s1.ariba.com" className={inputClass} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Usuario</label>
          <input value={usuario} onChange={(e) => setUsuario(e.target.value)} placeholder="supplier_test_01" className={inputClass} required />
        </div>
        <div>
          <label className={labelClass}>Contraseña{esEdicion && <span className="normal-case font-normal text-gray-500"> (dejar vacío para no cambiarla)</span>}</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={esEdicion ? '••••••••' : ''}
              className={`${inputClass} pr-9`}
              required={!esEdicion}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className={labelClass}>Nota / Comentario</label>
        <textarea value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Proveedor para pruebas E2E..." rows={2} className={inputClass} />
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
        <input type="checkbox" checked={compartida} onChange={(e) => setCompartida(e.target.checked)} className="w-4 h-4 rounded border-white/20 bg-zinc-800" />
        Compartir con el equipo del proyecto
        <span className="text-xs text-gray-500">(el equipo la ve en la lista y puede pedir la contraseña con aprobación; si no, solo tú la ves)</span>
      </label>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all">
          Cancelar
        </button>
        <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition-all">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : esEdicion ? 'Guardar cambios' : 'Crear credencial'}
        </button>
      </div>
    </form>
  );
}
